import { map } from "lodash";
import { OptionTypeInterface } from "../../app/styledComponents/drop-down/types";

import {
    FiltersType,
    SortingType,
    GSCPayloadType,
    ViewDataType,
    CreateSaveViewPayloadType,
    UpdateSaveViewPayloadType,
    BackendViewDataType,
    CountryInterface
} from "../duck/types";
import { COUNTRIES } from "./const";

export function generateFilterPayload(
    siteUrl: string,
    startDate: Date,
    endDate: Date,
    filters: FiltersType,
    sorting: SortingType,
    currentPage: number
): GSCPayloadType {
    const payload = {
        domain_url: siteUrl,
        start_date: startDate.toISOString().split("T")[0],
        end_date: endDate.toISOString().split("T")[0],
        keyword: filters.keywordsFilters,
        url: filters.urlsFilter,
        search_metrics: filters.metricsFilters,
        keyword_url_connector: filters.keywordUrlConnector.value,
        country_code: filters.countryCode.value,
        device_type: filters.deviceType.value,
        search_type: filters.searchType.value,
        sortby: sorting.column,
        sortorder: sorting.order,
        page: currentPage
    };
    return payload;
}

export function generateSaveViewPayload(
    viewData: ViewDataType,
    filters: FiltersType,
    title: string
): CreateSaveViewPayloadType {
    const payload = {
        title,
        project_id: viewData.project_id,
        domain_url: viewData.domain_url,
        filters: {
            country_code: filters.countryCode.value,
            device_type: filters.deviceType.value,
            search_type: filters.searchType.value,
            keyword_url_connector: filters.keywordUrlConnector.value,
            keyword: {
                connector: filters.keywordsFilters.connector,
                list: filters.keywordsFilters.list
            },
            url: {
                connector: filters.urlsFilter.connector,
                list: filters.urlsFilter.list
            },
            search_metrics: {
                connector: filters.metricsFilters.connector,
                list: filters.metricsFilters.list
            }
        }
    };
    return payload;
}

export function generateUpdateViewPayload(
    filters: FiltersType,
    title: string
): UpdateSaveViewPayloadType {
    const payload = {
        title,
        filters: {
            country_code: filters.countryCode.value,
            device_type: filters.deviceType.value,
            search_type: filters.searchType.value,
            keyword_url_connector: filters.keywordUrlConnector.value,
            keyword: {
                connector: filters.keywordsFilters.connector,
                list: filters.keywordsFilters.list
            },
            url: {
                connector: filters.urlsFilter.connector,
                list: filters.urlsFilter.list
            },
            search_metrics: {
                connector: filters.metricsFilters.connector,
                list: filters.metricsFilters.list
            }
        }
    };
    return payload;
}

export function convertDateToString(date: Date | null): string {
    if (!date) { return ""; }
    // Format the date as YYYY-MM-DD
    const datestr = date.toISOString().slice(0, 10);
    return datestr;
}

export function transformBackendViewData(
    view: BackendViewDataType
): ViewDataType {
    return {
        ...view,
        filters: {
            countryCode: getCountryByCode(view.filters.country_code) || {
                label: "us",
                name: "United States of America (USA)",
                value: "usa"
            },
            deviceType: {
                label: view.filters.device_type,
                value: view.filters.device_type?.toLowerCase(),
            },
            searchType: {
                label: view.filters.search_type,
                value: view.filters.search_type?.toLowerCase(),
            },
            keywordsFilters: {
                connector: view.filters.keyword.connector,
                list: view.filters.keyword.list,
            },
            urlsFilter: {
                connector: view.filters.url.connector,
                list: view.filters.url.list,
            },
            keywordUrlConnector: {
                label: view.filters.keyword_url_connector?.toLowerCase(),
                value: view.filters.keyword_url_connector,
            },
            metricsFilters: {
                connector: view.filters.search_metrics.connector,
                list: view.filters.search_metrics.list,
            },
        },
    };
}

export function formatDateString(datestr: string): string {
    try {
        const date = new Date(datestr);
        return date.toLocaleString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    } catch (error) {
        return datestr;
    }

}

export function getDownloadFileName(
    viewName: string,
    startDate: Date,
    endDate: Date
): string {
    const strippedViewName = viewName.replace(/[^a-zA-Z0-9_]/g, "_");
    return `GSC_${strippedViewName}_${startDate}_to_${endDate}.csv`;
}

export const getFormattedCountries = (locales: CountryInterface[]): OptionTypeInterface[] => {
    return map(locales, (locale: CountryInterface) => ({
        label: locale.country,
        name: locale.name,
        value: locale.code
    }));
};

export const getCountryByCode = (code: string): {
    label: string;
    name: string;
    value: string;
} | null => {
    const codeLower = code?.toLowerCase();
    const country = COUNTRIES.find((c: CountryInterface) => c.code === codeLower);
    if (country) {
        return {
            label: country.country,
            name: country.name,
            value: country.code
        };
    }
    return null;
};

export function getLastWeekDateRange(
    minDate?: string | null,
    maxDate?: string | null
): { startDate: Date | null, endDate: Date | null } {
    try {
        const lastWeekEnd = new Date();
        if (maxDate && new Date(maxDate) < lastWeekEnd) {
            lastWeekEnd.setTime(new Date(maxDate).getTime());
        }
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        if (minDate && new Date(minDate) > lastWeekStart) {
            lastWeekStart.setTime(new Date(minDate).getTime());
        }
        return { startDate: lastWeekStart, endDate: lastWeekEnd };
    } catch (error) {
        return { startDate: null, endDate: null };
    }
}
